import { useCallback, useEffect, useState } from 'react';
import { api, plusDays, today, type Product, type Slot } from '../api';

/**
 * Capacity, by date.
 *
 * Slots are per-date rather than a recurrence rule because the real operation
 * overrides individual days constantly — Ramadan timings, a vehicle off the
 * road, a private camp hire that takes the whole site. An override should be a
 * row you can edit, not an exception to a rule.
 *
 * The API refuses to cut capacity below what is already sold, so this screen
 * surfaces held-vs-total rather than letting someone discover it on save.
 */
export default function Slots() {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(plusDays(today(), 14));
  const [sku, setSku] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.products().then(setProducts).catch(() => undefined);
  }, []);

  const load = useCallback(() => {
    setError(null);
    api
      .slots(from, to, sku || undefined)
      .then(setSlots)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [from, to, sku]);

  useEffect(load, [load]);

  return (
    <>
      <h1>Capacity</h1>
      <p className="sub">
        One row per departure per date. Availability on the site is this number minus what is
        already held — including bookings sitting in an open checkout.
      </p>

      <div className="row card" style={{ marginBlockEnd: '1rem' }}>
        <label className="small muted" htmlFor="from">
          From
        </label>
        <input id="from" type="date" value={from} onChange={(e) => setFrom(e.currentTarget.value)} />
        <label className="small muted" htmlFor="to">
          To
        </label>
        <input id="to" type="date" value={to} onChange={(e) => setTo(e.currentTarget.value)} />
        <label className="small muted" htmlFor="sku">
          Safari
        </label>
        <select id="sku" value={sku} onChange={(e) => setSku(e.currentTarget.value)}>
          <option value="">All</option>
          {products.map((p) => (
            <option key={p.sku} value={p.sku}>
              {p.name}
            </option>
          ))}
        </select>
        <button onClick={load}>Refresh</button>
      </div>

      {error && <p className="err">{error}</p>}
      {!slots && <p className="muted">Loading…</p>}
      {slots && slots.length === 0 && (
        <p className="muted">
          No departures in that window. Create them with <code>python -m app.seed</code>, or add one
          below.
        </p>
      )}

      {slots && slots.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Time</th>
              <th>Safari</th>
              <th>Total</th>
              <th>Held</th>
              <th>Free</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {slots.map((s) => (
              <SlotRow key={s.id} slot={s} onSaved={load} />
            ))}
          </tbody>
        </table>
      )}

      <h2>Add or adjust a departure</h2>
      <AddSlot products={products} onSaved={load} />
    </>
  );
}

function SlotRow({ slot, onSaved }: { slot: Slot; onSaved: () => void }) {
  const [total, setTotal] = useState(String(slot.vehiclesTotal));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(blocked: boolean, reason: string | null) {
    setBusy(true);
    setError(null);
    try {
      await api.upsertSlot({
        sku: slot.sku,
        date: slot.date,
        startsAt: slot.startsAt,
        vehiclesTotal: Number(total),
        seatsPerVehicle: slot.seatsPerVehicle,
        blocked,
        blockedReason: reason,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className={slot.blocked ? 'blocked' : undefined}>
      <td className="num">{slot.date}</td>
      <td className="num">{slot.startsAt}</td>
      <td className="small">{slot.sku}</td>
      <td>
        <input
          type="number"
          min={slot.vehiclesHeld}
          value={total}
          onChange={(e) => setTotal(e.currentTarget.value)}
          style={{ width: '4.5rem' }}
          aria-label="Vehicles total"
        />
      </td>
      <td className="num">{slot.vehiclesHeld}</td>
      <td className="num">{slot.vehiclesAvailable}</td>
      <td className="small">
        {slot.blocked ? (
          <>
            <span className="pill cancelled">blocked</span>
            {slot.blockedReason && <div className="muted">{slot.blockedReason}</div>}
          </>
        ) : (
          <span className="pill confirmed">running</span>
        )}
      </td>
      <td>
        <div className="row">
          <button disabled={busy} onClick={() => void save(slot.blocked, slot.blockedReason)}>
            Save
          </button>
          {slot.blocked ? (
            <button disabled={busy} onClick={() => void save(false, null)}>
              Unblock
            </button>
          ) : (
            <button
              disabled={busy || slot.vehiclesHeld > 0}
              title={
                slot.vehiclesHeld > 0
                  ? 'Cancel the bookings on this departure first'
                  : 'Take this departure off sale'
              }
              onClick={() => {
                const reason = prompt('Why is this departure not running?', 'Camp taken by a private hire');
                if (reason) void save(true, reason);
              }}
            >
              Block
            </button>
          )}
        </div>
        {error && <p className="err small">{error}</p>}
      </td>
    </tr>
  );
}

function AddSlot({ products, onSaved }: { products: Product[]; onSaved: () => void }) {
  const [sku, setSku] = useState('');
  const [date, setDate] = useState(today());
  const [startsAt, setStartsAt] = useState('15:30');
  const [vehicles, setVehicles] = useState('4');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    setOk(false);
    try {
      await api.upsertSlot({ sku, date, startsAt, vehiclesTotal: Number(vehicles) });
      setOk(true);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="row">
        <select value={sku} onChange={(e) => setSku(e.currentTarget.value)} aria-label="Safari">
          <option value="">Choose a safari…</option>
          {products.map((p) => (
            <option key={p.sku} value={p.sku}>
              {p.name}
            </option>
          ))}
        </select>
        <input type="date" value={date} onChange={(e) => setDate(e.currentTarget.value)} aria-label="Date" />
        <input
          type="time"
          value={startsAt}
          onChange={(e) => setStartsAt(e.currentTarget.value)}
          aria-label="Local start time"
        />
        <input
          type="number"
          min={0}
          value={vehicles}
          onChange={(e) => setVehicles(e.currentTarget.value)}
          style={{ width: '5rem' }}
          aria-label="Vehicles"
        />
        <button className="primary" disabled={busy || !sku} onClick={() => void submit()}>
          {busy ? 'Saving…' : 'Save departure'}
        </button>
      </div>
      <p className="small muted" style={{ marginBlockEnd: 0 }}>
        Times are local (Asia/Dubai). Saving an existing date and time adjusts it rather than
        creating a duplicate.
      </p>
      {ok && <p className="ok small">Saved.</p>}
      {error && <p className="err small">{error}</p>}
    </div>
  );
}
